import { $getCommandMenuMarker } from '#pplx/frontend/composer-sdk/internal/typeahead/commandMenuMarker.js';
import { $addUpdateTag, $getSelection, $isElementNode, $isRangeSelection, $isTextNode, HISTORY_MERGE_TAG, } from 'lexical';
export function $getActiveCommandMenuQuery() {
    const marker = $getCommandMenuMarker();
    const textNode = marker?.getPreviousSibling();
    const parentNode = marker?.getParent();
    if (!marker || !parentNode || !$isElementNode(parentNode)) {
        return null;
    }
    const markerOffset = parentNode
        .getChildren()
        .findIndex((child) => child.getKey() === marker.getKey());
    if (markerOffset < 0) {
        return null;
    }
    if (!$isTextNode(textNode)) {
        if (marker.getQueryStartOffset() !== 0) {
            return null;
        }
        return { marker, markerOffset, parentNode, query: '', textNode: null };
    }
    const text = textNode.getTextContent();
    if (marker.getQueryStartOffset() > text.length) {
        return null;
    }
    return {
        marker,
        markerOffset,
        parentNode,
        query: text.slice(marker.getQueryStartOffset()),
        textNode,
    };
}
export function $isSelectionAtActiveQueryEnd(activeQuery) {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
        return false;
    }
    if (!activeQuery.textNode) {
        return (selection.anchor.getNode().getKey() === activeQuery.parentNode.getKey() &&
            selection.anchor.offset === activeQuery.markerOffset);
    }
    return (selection.anchor.getNode().getKey() === activeQuery.textNode.getKey() &&
        selection.anchor.offset === activeQuery.textNode.getTextContentSize());
}
/**
 * Whether an expanded selection covers at least one character of the query's
 * text. Selections elsewhere in the draft do not belong to the menu — keeping
 * it open would let the navigation handler commit a row on Enter instead of
 * acting on the visible selection.
 */
export function $expandedSelectionOverlapsQuery(activeQuery) {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || selection.isCollapsed()) {
        return false;
    }
    const { marker, textNode } = activeQuery;
    if (!textNode) {
        return false;
    }
    const textNodeKey = textNode.getKey();
    if (!selection.getNodes().some((node) => node.getKey() === textNodeKey)) {
        return false;
    }
    const [startPoint, endPoint] = selection.isBackward()
        ? [selection.focus, selection.anchor]
        : [selection.anchor, selection.focus];
    const queryEnd = textNode.getTextContentSize();
    const coveredFrom = startPoint.getNode().getKey() === textNodeKey ? startPoint.offset : 0;
    const coveredTo = endPoint.getNode().getKey() === textNodeKey ? endPoint.offset : queryEnd;
    return Math.max(coveredFrom, marker.getQueryStartOffset()) < Math.min(coveredTo, queryEnd);
}
/**
 * Backspace on an empty query closes the menu instead of eating the character
 * before it (which belongs to the draft, not the query).
 */
export function $deleteEmptyCommandMenuQuery() {
    const activeQuery = $getActiveCommandMenuQuery();
    if (!activeQuery || activeQuery.query.length > 0 || !$isSelectionAtActiveQueryEnd(activeQuery)) {
        return false;
    }
    const { marker, markerOffset, parentNode, textNode } = activeQuery;
    // This close removes only the marker, so it must not spend an undo step
    // (see the historic-tag handling in `useMarkerAnchoredCommandMenu`).
    $addUpdateTag(HISTORY_MERGE_TAG);
    marker.remove();
    if (textNode) {
        textNode.select(marker.getQueryStartOffset(), marker.getQueryStartOffset());
    }
    else {
        parentNode.select(markerOffset, markerOffset);
    }
    return true;
}
/**
 * Remove the query text and the marker, leaving the caret where the query
 * started — the first half of committing a selection, before the provider's
 * replacement nodes go in.
 */
export function $clearActiveCommandMenuQuery() {
    const activeQuery = $getActiveCommandMenuQuery();
    if (!activeQuery) {
        return false;
    }
    const { marker, markerOffset, parentNode, textNode } = activeQuery;
    const queryStartOffset = marker.getQueryStartOffset();
    if (textNode) {
        const text = textNode.getTextContent();
        textNode.setTextContent(text.slice(0, queryStartOffset));
        textNode.select(queryStartOffset, queryStartOffset);
    }
    else {
        parentNode.select(markerOffset, markerOffset);
    }
    marker.remove();
    return true;
}
//# sourceMappingURL=commandMenuQuery.js.map